import React, { createContext, useReducer } from 'react';
import _ from 'lodash';
import { AWSImage, Proforma, ProformaContent, ProformaSideSection, Side, Word } from '../types';
import { proformasAPI } from '../API/proformas';
import { getNewProformaFromState } from '../utils';

export enum ActionTypes {
  SET_PROFORMA = 'SET_PROFORMA',
  SET_CONTENT = 'SET_CONTENT',
  SET_LOADING = 'SET_LOADING',
  SET_ERROR = 'SET_ERROR',
  SET_DEFAULT_VALUE = 'SET_DEFAULT_VALUE',
  SET_VALUE = 'SET_VALUE',
  SET_SAVING = 'SET_SAVING',
  SET_NEW_PROFORMA = 'SET_NEW_PROFORMA',
  SET_NEW_EXEMPLAR = 'SET_NEW_EXEMPLAR',
  SET_SAVED = 'SET_SAVED',
  SET_UPDATED = 'SET_UPDATED',
  SET_IMAGE = 'SET_IMAGE',
  SET_IMAGE_CHANGED = 'SET_IMAGE_CHANGED',
  UPDATE_RELATIONSHIPS = 'UPDATE_RELATIONSHIPS',
  CLEAR_STATE = 'CLEAR_STATE',
}

const initialState: IProformaState = {
  isLoading: true,
  isError: false,
  content: {},
  proforma: null,
  defaultValue: {},
  value: {},
  images: {},
  defaultImages: [],
  isSaving: false,
  newProforma: null,
  newExemplar: null,
  isSaved: false,
  isUpdated: false,
  isImageChanged: false,
};

export interface IProformaState {
  content: ProformaContent | null;
  proforma: Proforma | null;
  isLoading: boolean;
  isError: boolean;
  defaultValue: Record<string, string>;
  value: Record<string, string>;
  images: Record<string, string>;
  defaultImages: AWSImage[];
  isSaving: boolean;
  newProforma: Proforma | null;
  newExemplar: Proforma | null;
  isSaved: boolean;
  isUpdated: boolean;
  isImageChanged: boolean;
}

type payload = string | boolean | unknown;

export interface IAction {
  type: string;
  payload: payload;
}

type Dispatch = {
  value: Record<string, string>;
  isError: boolean;
  isLoading: boolean;
  isSaving: boolean;
  content: ProformaContent | null;
  proforma: Proforma | null;
  newProforma: Proforma | null;
  newExemplar: Proforma | null;
  defaultImages: AWSImage[];
  isSaved: boolean;
  isUpdated: boolean;
  isFilled: boolean;
  isChanged: boolean;
  loadProforma: (proforma: Proforma) => void;
  handleChange: (id: string, value: string, lines: number) => void;
  getValueById: (id: string) => string;
  handleImagePick: (id: string, uuid: string) => void;
  handleIsImageChanged: () => void;
  getImageUUID: (id: string) => string;
  handleUpdateRelationships: (awsImages: AWSImage[]) => void;
  handleSaveProformaClick: (title: string, isSavedProforma: boolean) => void;
  handleSaveExemplarClick: (title: string, isSavedProforma: boolean) => void;
  handleUpdateProforma: () => void;
  checkExemplarValue: (keys: string[]) => boolean;
  setError: (isError: boolean) => void;
  setSaved: (isSaved: boolean) => void;
  setUpdated: (isUpdated: boolean) => void;
  getExemplarContent: () => Side;
};

// eslint-disable-next-line complexity
const proformaReducer = (state: IProformaState, action: IAction): IProformaState => {
  const { type, payload } = action;

  switch (type) {
    case ActionTypes.SET_CONTENT:
      return {
        ...state,
        content: payload as ProformaContent,
      };
    case ActionTypes.SET_PROFORMA:
      return {
        ...state,
        proforma: payload as Proforma,
      };
    case ActionTypes.SET_NEW_PROFORMA:
      return {
        ...state,
        newProforma: payload as Proforma,
      };
    case ActionTypes.SET_NEW_EXEMPLAR:
      return {
        ...state,
        newExemplar: payload as Proforma,
      };
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: payload as boolean,
      };
    case ActionTypes.SET_ERROR:
      return {
        ...state,
        isError: payload as boolean,
      };
    case ActionTypes.SET_DEFAULT_VALUE:
      return {
        ...state,
        defaultValue: payload as Record<string, string>,
      };
    case ActionTypes.SET_VALUE:
      return {
        ...state,
        value: payload as Record<string, string>,
      };
    case ActionTypes.SET_SAVING:
      return {
        ...state,
        isSaving: payload as boolean,
      };
    case ActionTypes.SET_SAVED:
      return {
        ...state,
        isSaved: payload as boolean,
      };
    case ActionTypes.SET_UPDATED:
      return {
        ...state,
        isUpdated: payload as boolean,
      };
    case ActionTypes.SET_IMAGE:
      return {
        ...state,
        images: payload as Record<string, string>,
      };
    case ActionTypes.SET_IMAGE_CHANGED:
      return {
        ...state,
        isImageChanged: payload as boolean,
      };
    case ActionTypes.UPDATE_RELATIONSHIPS:
      return {
        ...state,
        defaultImages: payload as AWSImage[],
      };
    case ActionTypes.CLEAR_STATE:
      return initialState;
    default:
      return state;
  }
};

export const ProformaContext = createContext<Dispatch>({
  value: {},
  isError: false,
  isLoading: true,
  isSaving: false,
  isSaved: false,
  isUpdated: false,
  newProforma: null,
  newExemplar: null,
  isFilled: false,
  isChanged: false,
  content: {},
  proforma: null,
  defaultImages: [],
  handleChange: () => false,
  loadProforma: () => false,
  getValueById: () => '',
  handleImagePick: () => false,
  handleIsImageChanged: () => false,
  getImageUUID: () => '',
  handleUpdateRelationships: () => false,
  handleSaveProformaClick: () => false,
  handleSaveExemplarClick: () => false,
  handleUpdateProforma: () => false,
  checkExemplarValue: () => false,
  setError: () => false,
  setSaved: () => false,
  setUpdated: () => false,
  getExemplarContent: () => ({
    right: [],
    left: [],
    bottom: [],
  }),
});

export const ProformaContextProvider: React.FC = ({ children }): JSX.Element => {
  const [state, dispatch] = useReducer(proformaReducer, initialState);

  const loadProforma = (proforma: Proforma) => {
    dispatch({ type: ActionTypes.CLEAR_STATE, payload: undefined });
    dispatch({ type: ActionTypes.SET_IMAGE_CHANGED, payload: false });
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });

    let values: Record<string, string> = {};
    let text = '';
    for (const contentSide in proforma.content) {
      proforma.content[contentSide].map((section, index) => {
        if (!('lines' in section)) return;

        (section as ProformaSideSection).lines.map((line, lineIndex: number) => {
          if (!line.words.length) return;

          line.words.map((word: Word, wordIndex: number) => {
            const lineWordsLength = line.words.length;

            text = text.concat(word.text || '');

            const isTheLastWordInTheLine = wordIndex === lineWordsLength - 1;
            const isTheLastLineInTheSection = lineIndex === (section?.lines?.length as number) - 1;
            const isNextLineWithWords = section.lines[lineIndex + 1] && section.lines[lineIndex + 1].words.length;

            if (isTheLastWordInTheLine && !isTheLastLineInTheSection && isNextLineWithWords) {
              text = text.concat('\n');
              return;
            }
          });
        });

        values = { ...values, [`${contentSide}-${index}`]: text };
        text = '';
      });

      dispatch({ type: ActionTypes.SET_DEFAULT_VALUE, payload: { ...values } });
      dispatch({ type: ActionTypes.SET_VALUE, payload: { ...values } });
    }

    dispatch({ type: ActionTypes.SET_CONTENT, payload: { ...proforma.content } });
    dispatch({ type: ActionTypes.SET_PROFORMA, payload: { ...proforma } });
    dispatch({ type: ActionTypes.SET_LOADING, payload: false });
  };

  const getValueById = (id: string) => {
    return state.value[id] || '';
  };

  const getImageUUID = (id: string) => {
    return state.images[id] || '';
  };

  const handleChange = (id: string, value: string, lines: number) => {
    const values = state.value;
    // Allow any en alpha, number, or special characters, do not allow other languages.
    const allowedChars = /^[~` \\!@#$%^&*()_+=[\]{}|;':",./<>?a-zA-Z0-9-\n\s]+$/;
    const rows = value.split(/\r?\n|\r/).length;

    if ((value.match(allowedChars) || !value) && rows <= lines) {
      dispatch({ type: ActionTypes.SET_VALUE, payload: { ...values, [id]: value } });
    }
  };

  const handleImagePick = (id: string, uuid: string) => {
    dispatch({ type: ActionTypes.SET_IMAGE, payload: { ...state.images, [id]: uuid } });
  };

  const handleIsImageChanged = () => {
    !state.isImageChanged && dispatch({ type: ActionTypes.SET_IMAGE_CHANGED, payload: true });
  };

  const setError = (isError: boolean) => {
    dispatch({ type: ActionTypes.SET_ERROR, payload: isError });
  };

  const setSaving = (isSaving: boolean) => {
    dispatch({ type: ActionTypes.SET_SAVING, payload: isSaving });
  };

  const setSaved = (isSaved: boolean) => {
    dispatch({ type: ActionTypes.SET_SAVED, payload: isSaved });
  };

  const setUpdated = (isUpdated: boolean) => {
    dispatch({ type: ActionTypes.SET_UPDATED, payload: isUpdated });
  };

  const handleSaveProformaClick = async (title: string, isSavedProforma: boolean) => {
    setSaving(true);
    setError(false);

    if (!state.proforma) return;

    const { value, images, defaultImages, proforma } = state;
    const content = getNewProformaFromState(value, images, defaultImages, proforma, false);

    try {
      const { data } = await proformasAPI[isSavedProforma ? 'saveProformaFromSaved' : 'saveProforma']({
        id: state.proforma.id,
        title: title,
        content: content,
      });

      dispatch({ type: ActionTypes.SET_NEW_PROFORMA, payload: { ...data.data.content, ...{ id: data.data.id } } });
      dispatch({ type: ActionTypes.SET_NEW_EXEMPLAR, payload: null });
      dispatch({ type: ActionTypes.SET_DEFAULT_VALUE, payload: state.value });
      setSaving(false);
      setSaved(true);
    } catch (error) {
      setError(true);
      setSaving(false);
    }
  };

  const getExemplarContent = () => {
    if (!state.proforma) return {} as Side;
    return getNewProformaFromState(state.value, state.images, state.defaultImages, state.proforma, true) as Side;
  };

  const handleSaveExemplarClick = async (title: string, isSavedProforma: boolean) => {
    setSaving(true);
    setError(false);

    if (!state.proforma) return;

    const content = getNewProformaFromState(state.value, state.images, state.defaultImages, state.proforma, true);

    try {
      const { data } = await proformasAPI[isSavedProforma ? 'saveExemplarFromSaved' : 'saveExemplar']({
        id: state.proforma.id,
        title: title,
        content: content,
      });

      const { value, images, defaultImages, proforma } = state;
      const proformaContentWithEmptyLines = getNewProformaFromState(value, images, defaultImages, proforma, false);
      const newExemplar = { ...data.data.content, content: proformaContentWithEmptyLines };

      dispatch({ type: ActionTypes.SET_NEW_EXEMPLAR, payload: { ...newExemplar, ...{ id: data.data.id } } });
      dispatch({ type: ActionTypes.SET_NEW_PROFORMA, payload: null });
      dispatch({ type: ActionTypes.SET_DEFAULT_VALUE, payload: state.value });
      setSaving(false);
      setSaved(true);
    } catch (error) {
      setError(true);
      setSaving(false);
    }
  };

  const handleUpdateProforma = async () => {
    setSaving(true);
    setError(false);

    if (!state.proforma) return;

    const { value, images, defaultImages, proforma } = state;
    const content = getNewProformaFromState(value, images, defaultImages, proforma, false);

    try {
      const { data } = await proformasAPI.updateSavedProforma({
        id: state.proforma.id,
        content: content,
      });

      dispatch({ type: ActionTypes.SET_NEW_PROFORMA, payload: { ...data.data.content, ...{ id: data.data.id } } });
      dispatch({ type: ActionTypes.SET_NEW_EXEMPLAR, payload: null });
      dispatch({ type: ActionTypes.SET_DEFAULT_VALUE, payload: state.value });
      setSaving(false);
      setUpdated(true);
    } catch (error) {
      setError(true);
      setSaving(false);
    }
  };

  const checkValue = () => {
    for (const [, value] of Object.entries(state.value)) {
      if (value) {
        return true;
      }
    }
    return false;
  };

  const checkIsChanged = () => {
    return !_.isEqual(state.value, state.defaultValue) || state.isImageChanged;
  };

  const checkExemplarValue = (sides: string[]): boolean => {
    const values: string[] = [];
    Object.entries(state.value).forEach(section => {
      if (section[1].length > 0) {
        values.push(section[0]);
      }
    });

    return !sides.every(side => values.includes(side));
  };

  const handleUpdateRelationships = (awsImages: AWSImage[]) => {
    dispatch({ type: ActionTypes.UPDATE_RELATIONSHIPS, payload: awsImages });
  };

  const getError = (): boolean => state.isError;
  const getLoading = (): boolean => state.isLoading;
  const getContent = (): ProformaContent | null => state.content && state.content;
  const getProforma = (): Proforma | null => state.proforma && state.proforma;
  const getNewProforma = (): Proforma | null => state.newProforma && state.newProforma;
  const getNewExemplar = (): Proforma | null => state.newExemplar && state.newExemplar;
  const getDefaultImages = (): AWSImage[] => state.defaultImages;
  const getSaving = (): boolean => state.isSaving;
  const getSaved = (): boolean => state.isSaved;
  const getUpdated = (): boolean => state.isUpdated;
  const getValue = (): Record<string, string> => state.value;

  return (
    <ProformaContext.Provider
      value={{
        isSaved: getSaved(),
        isUpdated: getUpdated(),
        isError: getError(),
        isLoading: getLoading(),
        loadProforma: loadProforma,
        content: getContent(),
        proforma: getProforma(),
        isSaving: getSaving(),
        newProforma: getNewProforma(),
        newExemplar: getNewExemplar(),
        defaultImages: getDefaultImages(),
        isFilled: checkValue(),
        isChanged: checkIsChanged(),
        handleSaveProformaClick: handleSaveProformaClick,
        handleSaveExemplarClick: handleSaveExemplarClick,
        handleUpdateProforma: handleUpdateProforma,
        checkExemplarValue,
        handleChange,
        value: getValue(),
        getValueById,
        setError,
        setSaved,
        setUpdated,
        handleImagePick,
        handleIsImageChanged,
        getImageUUID,
        handleUpdateRelationships,
        getExemplarContent,
      }}
    >
      {children}
    </ProformaContext.Provider>
  );
};
